import { CREATOR_SHOP_SORT, PRODUCT_LIST_SORT } from '../constant';
import { AddKeysToObject } from '../../../shared/type/types';
import { ProductThumbnailOption } from './thumbnail';
import { associateProductsDbtNotExistInVariablesDbt } from '../repository/associateProductsDbt';
import { ProductTagBaseProps } from '@services/MarppleShop/renderApp/components/cells/ProductTag/ProductTagBase.ts';

/**
 * 타입 임의로 수정 금지, 쿼리도 고려하여 수정 필요합니다.
 */
export type ProductListFetchOption = {
  limit: number;
  page?: number;
  cate_list_id?: number;
  cate_item_id?: number;
};

// do not export
const PRODUCT_CARD_TYPE = {
  NORMAL: 'NORMAL',
  REALTIME: 'REALTIME',
} as const;

export type ProductCardType = keyof typeof PRODUCT_CARD_TYPE | undefined;

export type ProductCardDefaultType = typeof PRODUCT_CARD_TYPE.NORMAL;

export type IsRealtime<T extends ProductCardType> = T extends typeof PRODUCT_CARD_TYPE.REALTIME
  ? true
  : false;

/**
 * associate 로 상품 목록을 조회했을 때 리턴되는 타입입니다.
 */
export type AssociatedProduct = Awaited<ReturnType<typeof associateProductsDbtNotExistInVariablesDbt>>[0];

type addKeyProduct = AddKeysToObject<
  Omit<Omit<AssociatedProduct, 'name_intl'>, 'base_product_name_intl'>,
  [
    ['store_name', string],
    // ['thumbnails', ProductThumbnails],
    ['thumbnail_ratio', number | undefined],
    ['review_score', number],
    ['review_count', number],
    ['quantity', number],
    ['order_count', number],
    ['store_id', number],
    ['place_idx', number | null | undefined],
  ]
>;
/**
 * `ProductCard` 컴포넌트를 그리는데 필요한 데이터 타입입니다.
 */
// eslint-disable-next-line no-use-before-define
export interface ProductCardData
  extends Omit<addKeyProduct, 'price_intl' | 'user_id' | 'price_en' | 'price_jp' | 'is_use_creator_intl'> {
  name: string;
  price: number;
  cate_item_name: string; // TODO: 실제 데이터로 변경

  /**
   * TODO: `user_id: number | null`로 변경하기
   */
  user_id?: number | null;
  is_liked?: boolean;
  ranking?: number;
  out_of_stock: boolean;

  /**
   * `실시간` 뱃지 그릴 때 사용
   */
  ordered_at?: Date;

  /**
   * @description `Membership` 뱃지 그릴 때 사용
   */
  is_membership: boolean;

  /**
   * "n명이 보는 중" 뱃지 그릴 때 주입
   */
  watcher_count?: number;

  is_public: boolean;

  /**
   * 급상승
   */
  is_trending: boolean;

  /**
   * 인기
   */
  is_popular: boolean;
}

export enum ProductCardTheme {
  light = 'light',
  dark = 'dark',
}

/**
 * 수평형 카드의 경우 xSmall 사이즈만 지원합니다.
 */
export enum ProductCardSize {
  xSmall = 'xSmall',
  small = 'small',
  medium = 'medium',
  large = 'large',
}

/**
 * `ProductCard` 컴포넌트를 그리는데 필요한 옵션 타입입니다.
 */
export type ProductCardOption = ProductThumbnailOption & {
  size: ProductCardSize;

  /**
   * @default light
   */
  theme?: ProductCardTheme;

  /**
   * @description 뱃지 목록 숨기기
   * @default false
   */
  hide_badges?: boolean;

  /**
   * @description 하단 태그 숨기기
   * @default false
   */
  hide_tag?: boolean;

  /**
   * @description 리뷰 숨기기
   * @default false
   *
   * - 리뷰가 존재하지 않는 사이즈에서는 활성화해도 리뷰 영역이 보이지 않습니다.
   * - 리뷰가 존재하는 사이즈에서 활성화하면 리뷰 영역을 가립니다.
   */
  hide_review?: boolean;

  /**
   * 스토어명, 상품명, 가격 등 하단 정보 숨기는 옵션
   *
   * 이 옵션을 켜면 좋아요 버튼이 우측 상단에 보여집니다.
   * @default false
   */
  hide_info?: boolean;

  /**
   * 수평형 카드, 현재 사이즈는 xSmall만 지원합니다.
   */
  is_horizontal?: boolean;

  /**
   * hidden 상태로 컴포넌트 생성할 때 활성화
   */
  is_hidden?: boolean;

  /**
   * 카드 루트에 주입할 클래스
   */
  card_klass?: string;

  realtime_tag?: ProductTagBaseProps;

  injectedStyles?: string[];
};

/**
 * `ProductCardList` 컴포넌트를 그리는데 필요한 데이터 타입입니다.
 */
export type ProductCardListData = {
  products: ProductCardData[];
};

/**
 * paginated 상품 목록을 조회했을 때 응답 데이터입니다.
 */
export type PaginatedProductListResponse = {
  products: ProductCardData[];
  total_count: number;
};

export type ProductResponse = {
  product: ProductCardData | null;
};

export type DefaultProductListSort = keyof typeof PRODUCT_LIST_SORT;
export type CreatorShopProductListSort = keyof typeof CREATOR_SHOP_SORT;

export interface SortOption {
  key: keyof typeof PRODUCT_LIST_SORT;
  value: string;
  name: string;
}

export interface CreatorProductSortOption {
  key: keyof typeof CREATOR_SHOP_SORT;
  value: string;
  name: string;
}

export type UserLikedProductListFetchOption = {
  limit: number;
  offset?: number;
};

export type UserLikedProductListReqData = {
  limit?: string;
  offset?: string;
};

export const bestProductTypeMap = {
  realtime: 'realtime',
  weekly: 'weekly',
  monthly: 'monthly',
} as const;

export type BestProductType = (typeof bestProductTypeMap)[keyof typeof bestProductTypeMap];
export interface ProductSalesCount {
  id: number;
  count: number;
}

export const PRODUCT_SALE_STATUS = {
  /**
   * 판매 시작 전
   */
  BEFORE_SALE: 'BEFORE_SALE',

  /**
   * 판매 중 (상시 판매)
   */
  ON_SALE: 'ON_SALE',

  /**
   * 판매 중 (한정 판매)
   */
  LIMITED_SALE: 'LIMITED_SALE',

  /**
   * 판매 종료
   */
  ENDED_SALE: 'ENDED_SALE',
} as const;

export type ProductSaleStatusType = (typeof PRODUCT_SALE_STATUS)[keyof typeof PRODUCT_SALE_STATUS];
